import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Menu, MenuItem, Typography, Avatar } from '@mui/material';
import { Person, Logout } from '@mui/icons-material';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import { useAuth } from '../state/AuthContext';
import './common/HeaderStyles.css';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ADD_NEW_ACCOUNT, customArrayOperations, endOfDayForDateObj, getScopedItem,
  normalizeRoutePath, PLATFORM, removeScopedItem, setScopedItem,
  STORAGE_KEYS, SUBSCRIPTION_STATUS, USER_ROLE
} from './common/Helper';
import DebugService from '../services/DebugService';
import SubscriptionApiService from '../services/SubscriptionApiService';
import { useZustStore } from '../state/Zuststore';

const MainHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    userDetails, logout, selectedAccount, setSelectedAccount,
    accountList, platformList, selectedPlatform, setSelectedPlatform,
    loadingUserDetails,
  } = useAuth();

  const [anchorEl, setAnchorEl] = useState(null);
  const [subscriptionCache, setSubscriptionCache] = useState(new Map());
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [acctDropOpen, setAcctDropOpen] = useState(false);
  const [acctSearch, setAcctSearch] = useState('');
  const acctSelRef = useRef(null);
  const abortControllerRef = useRef(null);
  const pathnameRef = useRef(location.pathname);
  const { toggleChatbot } = useZustStore();

  const platformAccountList = useMemo(() => {
    if (!selectedPlatform || !accountList?.length) return [];
    return accountList.filter(a => a?.platformEnum === selectedPlatform);
  }, [selectedPlatform, accountList]);

  const currentAccount = useMemo(
    () => platformAccountList.find(a => a.accountId === selectedAccount),
    [platformAccountList, selectedAccount]
  );

  const filteredAccounts = useMemo(() => {
    const q = acctSearch.toLowerCase();
    return platformAccountList.filter(a =>
      a.accountId?.toLowerCase().includes(q) || a.accountAliase?.toLowerCase().includes(q)
    );
  }, [platformAccountList, acctSearch]);

  const userInitials = useMemo(() => {
    const f = (userDetails['firstname'] || '').charAt(0).toUpperCase();
    const l = (userDetails['lastname'] || '').charAt(0).toUpperCase();
    return (f + l) || 'U';
  }, [userDetails]);

  /* ── keep pathnameRef current without adding location to callback deps ── */
  useEffect(() => { pathnameRef.current = location.pathname; }, [location.pathname]);

  /* ── close dropdown on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (acctSelRef.current && !acctSelRef.current.contains(e.target)) {
        setAcctDropOpen(false);
        setAcctSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── cleanup on unmount ── */
  useEffect(() => {
    return () => { if (abortControllerRef.current) abortControllerRef.current.abort(); };
  }, []);

  /* ── profile/logout menu ── */
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleProfileClick = () => {
    setScopedItem(STORAGE_KEYS.CURRENT_ROUTE, CONST_ROUTE_PATH.MY_PROFILE);
    handleMenuClose();
    navigate(CONST_ROUTE_PATH.MY_PROFILE);
  };
  const handleLogoutClick = () => { handleMenuClose(); logout(); navigate(CONST_ROUTE_PATH.SIGN_IN); };

  /* ── subscription validation (unchanged logic) ── */
  const validateSubscription = useCallback(async (accountId) => {
    if (!accountId || accountId === ADD_NEW_ACCOUNT) return false;
    const cacheKey = `${accountId}_${Date.now() - (Date.now() % 300000)}`;
    if (subscriptionCache.has(cacheKey)) return subscriptionCache.get(cacheKey);
    try {
      setIsCheckingSubscription(true);
      const userId = getScopedItem('userID');
      const response = await SubscriptionApiService.getPurchasedPlanList({ userId, selectedAccount: accountId });
      const userSubscriptionData = response?.data ?? [];
      let isSubscribed = false;
      if (userSubscriptionData.length > 0) {
        const { latestRecord } = customArrayOperations(userSubscriptionData, 'createdOn');
        if (latestRecord) {
          setScopedItem(STORAGE_KEYS.SUBSCRIPTION_STATUS, latestRecord['status']);
          if ([SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(latestRecord.status)) {
            isSubscribed = latestRecord.currentEnd
              ? endOfDayForDateObj(new Date(latestRecord.currentEnd)) >= new Date()
              : true;
          }
        }
      }
      const newCache = new Map(subscriptionCache);
      newCache.set(cacheKey, isSubscribed);
      setSubscriptionCache(newCache);
      return isSubscribed;
    } catch { return true; }
    finally { setIsCheckingSubscription(false); }
  }, [subscriptionCache]);

  const accountSelectHandler = useCallback((newAcct) => {
    if (newAcct === selectedAccount) return;
    setSelectedAccount(newAcct);
    removeScopedItem(STORAGE_KEYS.CURRENT_ROUTE);
    if (newAcct !== ADD_NEW_ACCOUNT) {
      setScopedItem(STORAGE_KEYS.ACCOUNT_ID, newAcct);
      navigate(CONST_ROUTE_PATH.DASHBOARD);
    } else {
      navigate(CONST_ROUTE_PATH.ADD_ACCOUNT);
    }
  }, [selectedAccount, navigate, setSelectedAccount]);

  const syncPlatformAccount = useCallback(() => {
    if (!selectedPlatform || !platformAccountList.length) { setSelectedAccount(ADD_NEW_ACCOUNT); return; }
    const storedPlatform = getScopedItem(STORAGE_KEYS.PLATFORM) ?? '';
    const storedAccount = getScopedItem(STORAGE_KEYS.ACCOUNT_ID) ?? '';
    const accountExists = platformAccountList.some(a => a.accountId === storedAccount);
    if (storedAccount && storedPlatform === selectedPlatform && accountExists) {
      setSelectedAccount(storedAccount);
    } else {
      const first = platformAccountList[0];
      setScopedItem(STORAGE_KEYS.PLATFORM, selectedPlatform);
      setScopedItem(STORAGE_KEYS.ACCOUNT_ID, first.accountId);
      setSelectedAccount(first.accountId);
      removeScopedItem(STORAGE_KEYS.CURRENT_ROUTE);
      navigate(CONST_ROUTE_PATH.DASHBOARD);
    }
  }, [selectedPlatform, platformAccountList, navigate]);

  const handleSubscriptionNavigation = useCallback(async (accountId) => {
    if (!accountId || accountId === ADD_NEW_ACCOUNT) return;
    try {
      const isSubscribed = await validateSubscription(accountId);
      if (!isSubscribed) {
        // Azure accounts always show the dashboard regardless of subscription state
        if (selectedPlatform === PLATFORM.AZURE) {
          navigate(CONST_ROUTE_PATH.DASHBOARD);
        } else {
          navigate(CONST_ROUTE_PATH.PRICING);
        }
      } else {
        const storedRoute = getScopedItem(STORAGE_KEYS.CURRENT_ROUTE);
        if (storedRoute) {
          const norm = normalizeRoutePath(storedRoute);
          if (norm !== normalizeRoutePath(pathnameRef.current)) navigate(norm);
        }
      }
    } catch { DebugService.error('MainHeader: subscription nav error'); }
  }, [validateSubscription, navigate, selectedPlatform]);

  useEffect(() => {
    if (selectedPlatform != null) syncPlatformAccount();
  }, [selectedPlatform, syncPlatformAccount]);

  useEffect(() => {
    if (selectedAccount && selectedAccount !== ADD_NEW_ACCOUNT) {
      handleSubscriptionNavigation(selectedAccount);
    } else if (
      selectedAccount === ADD_NEW_ACCOUNT &&
      loadingUserDetails === false &&   // user data fully loaded — not still mid-fetch
      !platformAccountList.length       // confirmed: no accounts for this platform
    ) {
      // Only redirect to /add-account once we know the account list is genuinely empty,
      // not just because fetchUserData hasn't finished yet.
      navigate(CONST_ROUTE_PATH.ADD_ACCOUNT);
    }
  }, [selectedAccount, handleSubscriptionNavigation, loadingUserDetails, platformAccountList]);

  /* ── platform dot color ── */
  const platformDotColor = selectedPlatform === PLATFORM.AWS ? '#f57c00' : selectedPlatform === 'AZURE' ? '#0284c7' : '#34a853';

  return (
    <div className="main-header">
      <div className="header-controls">

        {/* Platform toggle pill */}
        {platformList?.length > 0 && (
          <div className="hdr-toggle" style={{ position: 'relative' }}>
            <span className="hdr-dot" style={{ background: platformDotColor }} />
            {selectedPlatform}
            <select
              value={selectedPlatform}
              onChange={e => setSelectedPlatform(e.target.value)}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', fontSize: '13px' }}
            >
              {platformList.map(p => <option key={p.id} value={p.enum}>{p.label}</option>)}
            </select>
          </div>
        )}

        {/* Single / All Accounts segmented control */}
        <div className="hdr-seg">
          <button className="active">◉ Single Account</button>
          <button disabled title="All Accounts view is not available yet">∑ All Accounts</button>
        </div>

        {/* Account selector */}
        <div className="hdr-acct-sel" ref={acctSelRef} onClick={() => setAcctDropOpen(o => !o)}>
          <div style={{ flex: 1 }}>
            <small>Account</small>
            <strong style={{ display: 'block', marginTop: 1 }}>
              {currentAccount
                ? `${currentAccount.accountId} — ${currentAccount.accountAliase}`
                : selectedAccount || '—'}
            </strong>
          </div>
          <span style={{ color: 'var(--muted)', fontSize: 11 }}>▾</span>

          {acctDropOpen && (
            <div className="hdr-acct-drop" onClick={e => e.stopPropagation()}>
              <input
                className="hdr-acct-search"
                placeholder="Search accounts…"
                value={acctSearch}
                onChange={e => setAcctSearch(e.target.value)}
                autoFocus
              />
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                {filteredAccounts.map(a => (
                  <div
                    key={a.accountId}
                    className={`hdr-acct-row${a.accountId === selectedAccount ? ' sel' : ''}`}
                    onClick={() => { accountSelectHandler(a.accountId); setAcctDropOpen(false); setAcctSearch(''); }}
                  >
                    <span className="hdr-acct-name">
                      {a.accountAliase}
                      <small>{a.accountId}</small>
                    </span>
                  </div>
                ))}
                {userDetails['roleName'] !== USER_ROLE.READ_ONLY && (
                  <div
                    className="hdr-acct-row add"
                    onClick={() => { accountSelectHandler(ADD_NEW_ACCOUNT); setAcctDropOpen(false); }}
                  >
                    ＋ Add Account
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Ask CloudsIQ — margin-left:auto pushes it and avatar to far right */}
        <button className="iq-btn" style={{ marginLeft: 'auto' }} onClick={toggleChatbot}>✦ Ask CloudsIQ</button>

        {/* User avatar */}
        <div className="hdr-avatar" onClick={handleMenuOpen}>{userInitials}</div>

        {/* User dropdown menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: '16px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,.1),0 10px 10px -5px rgba(0,0,0,.04)',
              border: '1px solid #e2e8f0',
              minWidth: 280, padding: '8px', marginTop: '8px',
            },
            '& .MuiList-root': { padding: 0 },
          }}
        >
          <Box sx={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 48, height: 48, background: 'linear-gradient(135deg,#3b82f6,#1e40af)', fontSize: 18, fontWeight: 600, border: '3px solid #e0f2fe' }}>
                {(userDetails['firstname'] || 'U').charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#1e293b', lineHeight: 1.2 }}>
                  {userDetails['firstname'] || 'User'} {userDetails['lastname'] || ''}
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#64748b', mt: '2px' }}>
                  {userDetails['email'] || ''}
                </Typography>
              </Box>
            </Box>
          </Box>

          <MenuItem onClick={handleProfileClick} sx={{ borderRadius: '12px', mx: 1, fontSize: 14, fontWeight: 500, color: '#374151', gap: 2, '&:hover': { backgroundColor: '#f0f9ff', color: '#0369a1' } }}>
            <Person sx={{ fontSize: 18 }} /> My Profile
          </MenuItem>

          <Box sx={{ height: 1, background: 'linear-gradient(90deg,transparent,#e2e8f0,transparent)', mx: 2, my: 1 }} />

          <MenuItem onClick={handleLogoutClick} sx={{ borderRadius: '12px', mx: 1, mb: 1, fontSize: 14, fontWeight: 500, color: '#dc2626', gap: 2, '&:hover': { backgroundColor: '#fef2f2', color: '#b91c1c' } }}>
            <Logout sx={{ fontSize: 18 }} /> Sign Out
          </MenuItem>
        </Menu>

      </div>
    </div>
  );
};

export default MainHeader;
